import { Box, IconButton, Typography } from "@mui/material"
import { KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"

import Chip from "@/components/common/Chip/Chip"
import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import type { ProductSourceChangeEvent } from "@/graphql/codegen/graphql"
import {
  PRODUCT_SOURCE_CHANGE_STATUS_TO_CHIP_CLASS_NAME,
  PRODUCT_SOURCE_CHANGE_STATUS_TO_LABEL,
} from "@/screens/Products/components/SourceChanges/constants"
import { formatDate } from "@/utils/date"

export const useSourceChangeHistoryColumns = (): ColumnDef<ProductSourceChangeEvent>[] => [
  {
    accessorKey: "processedOn",
    header: "Date Processed",
    cell: ({ row }) => formatDate(row.original.processedOn),
    enableSorting: false,
  },
  {
    accessorKey: "newSourcePublicId",
    header: "New Source",
    cell: ({ row }) => (
      <Box className='flex flex-col items-start gap-y-1'>
        <Box className='flex items-center gap-x-2'>
          <Box className='size-2 rounded-full bg-success-500' />
          <Typography variant='body2'>{row.original.newSourceProduct?.publicId}</Typography>
        </Box>
        <DisplayAS400Id
          id={row.original.newSourceProduct?.externalSystemId}
          data-testid={`source-change.history-table.as400-id-${row.original.sourceChangeBatchId}`}
        />
      </Box>
    ),
    enableSorting: false,
  },
  {
    accessorKey: "actingUserName",
    header: "Acting User",
    cell: ({ row }) => row.original.processedBy?.name ?? null,
    enableSorting: false,
  },
  {
    accessorKey: "customers",
    header: "Number of Customers Affected",
    cell: ({ row }) => row.original.customersAffected,
    enableSorting: false,
  },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => (
      <Chip
        size='small'
        className={`text-xs leading-[18px] ${PRODUCT_SOURCE_CHANGE_STATUS_TO_CHIP_CLASS_NAME[row.original.status]}`}
        label={PRODUCT_SOURCE_CHANGE_STATUS_TO_LABEL[row.original.status]}
        data-testid={`source-change.history-table.status-chip-${row.original.sourceChangeBatchId}`}
      />
    ),
    enableSorting: false,
  },
  {
    accessorKey: "actions",
    header: "",
    size: 50,
    cell: ({ row }) => (
      <Box className='flex justify-end'>
        <IconButton
          size='small'
          onClick={row.getToggleExpandedHandler()}
          aria-expanded={row.getIsExpanded()}
          aria-label='show customers'
          data-testid={`source-change.history-table.expand-button-${row.original.sourceChangeBatchId}`}
        >
          {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
        </IconButton>
      </Box>
    ),
    enableSorting: false,
  },
]
